import { 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy,
  startAt,
  endAt
} from 'firebase/firestore';
import { db } from './config';

export const analyticsService = {
  // Obtener reservas en un rango de fechas
  getReservationsByDateRange: async (startDate, endDate) => {
    try {
      const reservationsRef = collection(db, 'reservations');
      
      // Convertir fechas al formato DD/MM/YYYY
      const formatDate = (date) => {
        return new Date(date).toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      };

      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(endDate);
      
      // Crear array de fechas para consultar
      const dateQueries = [];
      const currentDate = new Date(startDate);
      const endDateObj = new Date(endDate);
      
      while (currentDate <= endDateObj) {
        const dateStr = formatDate(currentDate);
        dateQueries.push(dateStr);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Obtener reservas para todas las fechas
      const allReservations = [];
      for (const dateStr of dateQueries) {
        const q = query(
          reservationsRef,
          where('date', '==', dateStr)
        );
        const snapshot = await getDocs(q);
        const dayReservations = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        allReservations.push(...dayReservations);
      }
      
      return allReservations;
    } catch (error) {
      console.error('Error getting reservations by date range:', error);
      throw error;
    }
  },

  // Calcular estadísticas avanzadas
  calculateAdvancedStats: async (startDate, endDate) => {
    try {
      const reservations = await analyticsService.getReservationsByDateRange(startDate, endDate);
      
      // Filtrar por estado (excluir canceladas)
      const activeReservations = reservations.filter(r => 
        r.status !== 'RESERVA CANCELADA' && r.status !== 'NO SHOW'
      );
      
      // Separar por comidas y cenas
      const lunchReservations = activeReservations.filter(r => {
        const hour = parseInt(r.time.split(':')[0]);
        return hour < 17;
      });
      
      const dinnerReservations = activeReservations.filter(r => {
        const hour = parseInt(r.time.split(':')[0]);
        return hour >= 17;
      });
      
      // Calcular estadísticas por período
      const totalDays = Math.max(1, Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1);
      
      return {
        overview: {
          totalReservations: activeReservations.length,
          totalGuests: activeReservations.reduce((sum, r) => sum + (parseInt(r.guests) || 0), 0),
          averageReservationsPerDay: Math.round(activeReservations.length / totalDays * 10) / 10,
          averageGuestsPerDay: Math.round(activeReservations.reduce((sum, r) => sum + (parseInt(r.guests) || 0), 0) / totalDays * 10) / 10,
          averagePartySize: Math.round(activeReservations.reduce((sum, r) => sum + (parseInt(r.guests) || 0), 0) / Math.max(1, activeReservations.length) * 10) / 10,
          totalDays
        },
        byMealTime: {
          lunch: {
            totalReservations: lunchReservations.length,
            totalGuests: lunchReservations.reduce((sum, r) => sum + (parseInt(r.guests) || 0), 0),
            averagePerDay: Math.round(lunchReservations.length / totalDays * 10) / 10,
            averagePartySize: Math.round(lunchReservations.reduce((sum, r) => sum + (parseInt(r.guests) || 0), 0) / Math.max(1, lunchReservations.length) * 10) / 10
          },
          dinner: {
            totalReservations: dinnerReservations.length,
            totalGuests: dinnerReservations.reduce((sum, r) => sum + (parseInt(r.guests) || 0), 0),
            averagePerDay: Math.round(dinnerReservations.length / totalDays * 10) / 10,
            averagePartySize: Math.round(dinnerReservations.reduce((sum, r) => sum + (parseInt(r.guests) || 0), 0) / Math.max(1, dinnerReservations.length) * 10) / 10
          }
        },
        byStatus: {
          confirmed: reservations.filter(r => r.status === 'RESERVA CONFIRMADA').length,
          pending: reservations.filter(r => r.status === 'PENDIENTE DE CONFIRMACION').length,
          seated: reservations.filter(r => r.status === 'SENTADA').length,
          completed: reservations.filter(r => r.status === 'LIBERADA').length,
          cancelled: reservations.filter(r => r.status === 'RESERVA CANCELADA').length,
          noShow: reservations.filter(r => r.status === 'NO SHOW').length
        },
        bySource: {
          web: reservations.filter(r => r.source === 'web').length,
          google: reservations.filter(r => r.source === 'google').length,
          walkIn: reservations.filter(r => r.source === 'walk-in').length,
          admin: reservations.filter(r => r.source === 'admin').length,
          special: reservations.filter(r => r.source === 'special').length
        }
      };
    } catch (error) {
      console.error('Error calculating advanced stats:', error);
      throw error;
    }
  },

  // Calcular datos para gráficos diarios
  getDailyChartData: async (startDate, endDate) => {
    try {
      const reservations = await analyticsService.getReservationsByDateRange(startDate, endDate);
      
      // Crear array de fechas
      const dates = [];
      const currentDate = new Date(startDate);
      const endDateObj = new Date(endDate);
      
      while (currentDate <= endDateObj) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Agrupar datos por fecha
      const chartData = dates.map(date => {
        const dateStr = date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        
        const dayReservations = reservations.filter(r => r.date === dateStr);
        const activeReservations = dayReservations.filter(r => 
          r.status !== 'RESERVA CANCELADA' && r.status !== 'NO SHOW'
        );
        
        const lunchReservations = activeReservations.filter(r => {
          const hour = parseInt(r.time.split(':')[0]);
          return hour < 17;
        });
        
        const dinnerReservations = activeReservations.filter(r => {
          const hour = parseInt(r.time.split(':')[0]);
          return hour >= 17;
        });
        
        return {
          date: date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
          fullDate: dateStr,
          totalReservations: activeReservations.length,
          totalGuests: activeReservations.reduce((sum, r) => sum + (parseInt(r.guests) || 0), 0),
          lunchReservations: lunchReservations.length,
          dinnerReservations: dinnerReservations.length,
          lunchGuests: lunchReservations.reduce((sum, r) => sum + (parseInt(r.guests) || 0), 0),
          dinnerGuests: dinnerReservations.reduce((sum, r) => sum + (parseInt(r.guests) || 0), 0)
        };
      });
      
      return chartData;
    } catch (error) {
      console.error('Error getting daily chart data:', error);
      throw error;
    }
  },

  // Calcular nivel de ocupación estimado
  calculateOccupancyLevel: async (startDate, endDate) => {
    try {
      const reservations = await analyticsService.getReservationsByDateRange(startDate, endDate);
      
      // Obtener todas las mesas para calcular capacidad máxima
      const tablesRef = collection(db, 'tables');
      const tablesSnapshot = await getDocs(tablesRef);
      const tables = tablesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const totalCapacity = tables.reduce((sum, table) => sum + (table.capacity || 3), 0);
      
      // Calcular días operativos (excluyendo días sin reservas si es necesario)
      const totalDays = Math.max(1, Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1);
      
      // Estimar turnos por día (2 turnos: comida y cena)
      const maxDailyCapacity = totalCapacity * 2;
      const maxPeriodCapacity = maxDailyCapacity * totalDays;
      
      const activeReservations = reservations.filter(r => 
        r.status !== 'RESERVA CANCELADA' && r.status !== 'NO SHOW'
      );
      
      const totalGuests = activeReservations.reduce((sum, r) => sum + (parseInt(r.guests) || 0), 0);
      
      const occupancyPercentage = Math.min(100, Math.round((totalGuests / maxPeriodCapacity) * 100));
      
      return {
        totalCapacity,
        maxDailyCapacity,
        maxPeriodCapacity,
        totalGuests,
        occupancyPercentage,
        averageDailyOccupancy: Math.round((totalGuests / totalDays / maxDailyCapacity) * 100)
      };
    } catch (error) {
      console.error('Error calculating occupancy level:', error);
      throw error;
    }
  },

  // NUEVA FUNCIÓN: Obtener métricas de marketing (promociones y origen)
  getMarketingMetrics: async (startDate, endDate) => {
    try {
      // Reutilizar la función existente para obtener reservas
      const reservations = await analyticsService.getReservationsByDateRange(startDate, endDate);
      
      const metrics = {
        byPromo: {},
        bySource: {},
        byCombo: {},
        totals: {
          withPromo: 0,
          withSource: 0,
          withBoth: 0,
          uniquePromos: 0,
          uniqueSources: 0
        },
        topPerformers: {
          bestPromo: null,
          bestSource: null,
          bestCombo: null
        }
      };
      
      // Procesar cada reserva
      reservations.forEach(reservation => {
        const promo = reservation.codigo_promo;
        const source = reservation.source || 'direct';
        
        // Métricas por código promocional
        if (promo) {
          metrics.totals.withPromo++;
          
          if (!metrics.byPromo[promo]) {
            metrics.byPromo[promo] = {
              count: 0,
              guests: 0,
              confirmed: 0,
              cancelled: 0
            };
          }
          
          metrics.byPromo[promo].count++;
          metrics.byPromo[promo].guests += parseInt(reservation.guests || 0);
          
          if (reservation.status === 'RESERVA CONFIRMADA') {
            metrics.byPromo[promo].confirmed++;
          } else if (reservation.status === 'RESERVA CANCELADA') {
            metrics.byPromo[promo].cancelled++;
          }
        }
        
        // Métricas por source (mejoradas para incluir todos los sources)
        if (source !== 'direct') {
          metrics.totals.withSource++;
        }
        
        if (!metrics.bySource[source]) {
          metrics.bySource[source] = {
            count: 0,
            guests: 0,
            confirmed: 0,
            cancelled: 0
          };
        }
        
        metrics.bySource[source].count++;
        metrics.bySource[source].guests += parseInt(reservation.guests || 0);
        
        if (reservation.status === 'RESERVA CONFIRMADA') {
          metrics.bySource[source].confirmed++;
        } else if (reservation.status === 'RESERVA CANCELADA') {
          metrics.bySource[source].cancelled++;
        }
        
        // Métricas combinadas (promo + source)
        if (promo && source) {
          metrics.totals.withBoth++;
          const comboKey = `${promo}_${source}`;
          
          if (!metrics.byCombo[comboKey]) {
            metrics.byCombo[comboKey] = {
              promo: promo,
              source: source,
              count: 0,
              guests: 0,
              confirmed: 0
            };
          }
          
          metrics.byCombo[comboKey].count++;
          metrics.byCombo[comboKey].guests += parseInt(reservation.guests || 0);
          
          if (reservation.status === 'RESERVA CONFIRMADA') {
            metrics.byCombo[comboKey].confirmed++;
          }
        }
      });
      
      // Calcular totales únicos
      metrics.totals.uniquePromos = Object.keys(metrics.byPromo).length;
      metrics.totals.uniqueSources = Object.keys(metrics.bySource).length;
      
      // Encontrar los mejores performers
      if (Object.keys(metrics.byPromo).length > 0) {
        metrics.topPerformers.bestPromo = Object.entries(metrics.byPromo)
          .reduce((best, [code, data]) => {
            if (!best || data.count > best.data.count) {
              return { code, data };
            }
            return best;
          }, null);
      }
      
      if (Object.keys(metrics.bySource).length > 0) {
        metrics.topPerformers.bestSource = Object.entries(metrics.bySource)
          .reduce((best, [source, data]) => {
            if (!best || data.count > best.data.count) {
              return { source, data };
            }
            return best;
          }, null);
      }
      
      if (Object.keys(metrics.byCombo).length > 0) {
        metrics.topPerformers.bestCombo = Object.values(metrics.byCombo)
          .reduce((best, combo) => {
            if (!best || combo.count > best.count) {
              return combo;
            }
            return best;
          }, null);
      }
      
      return metrics;
    } catch (error) {
      console.error('Error fetching marketing metrics:', error);
      throw error;
    }
  }
};

export default analyticsService;